import type { Node } from "@xyflow/react";
import type { CanvasGraph } from "./canvas-commands";
import { expandGroupSelection } from "./canvas-groups";
import { NODE_DEFAULT_SIZES, type BoardNodeType } from "./node-creation";

export type CanvasMode = "select" | "pan";
export type NavigationAction = "zoomIn" | "zoomOut" | "actualSize" | "fitBoard" | "fitSelection";
export const MIN_CANVAS_ZOOM = 0.5;
export const MAX_CANVAS_ZOOM = 2;
export function canvasInteraction(mode: CanvasMode, temporaryPan: boolean, editable: boolean) {
  const panning = mode === "pan" || temporaryPan;
  return { panning, selectionOnDrag: !panning, panOnDrag: panning ? [0, 1] : [1],
    elementsSelectable: !panning, nodesDraggable: editable && !panning,
    nodesConnectable: editable && !panning, edgesReconnectable: editable && !panning };
}
export function selectCanvas(graph: CanvasGraph, selected: boolean): CanvasGraph {
  const ids = selected ? expandGroupSelection(graph.nodes,
    graph.nodes.filter(node => !node.hidden && node.selectable !== false).map(node => node.id)) : new Set<string>();
  return { ...graph, nodes: graph.nodes.map(node => !!node.selected === ids.has(node.id) ? node : { ...node, selected: ids.has(node.id) }),
    edges: graph.edges.map(edge => {
      const next = selected && !edge.hidden && edge.selectable !== false;
      return !!edge.selected === next ? edge : { ...edge, selected: next };
    }) };
}
export function measurableNode(node: Node): boolean {
  const fallback = NODE_DEFAULT_SIZES[node.type as BoardNodeType];
  const width = node.measured?.width ?? node.width ?? node.style?.width ?? fallback?.width;
  const height = node.measured?.height ?? node.height ?? node.style?.height ?? fallback?.height;
  return !node.hidden && [width, height].every(value => typeof value === "number" && Number.isFinite(value) && value > 0);
}
export function navigationTargets(graph: CanvasGraph, selection: boolean): Node[] {
  if (!selection) return graph.nodes.filter(measurableNode);
  const selected = expandGroupSelection(graph.nodes, graph.nodes.filter(node => node.selected && !node.hidden).map(node => node.id));
  for (const edge of graph.edges) if (edge.selected && !edge.hidden) { selected.add(edge.source); selected.add(edge.target); }
  return graph.nodes.filter(node => selected.has(node.id) && measurableNode(node));
}
export function navigationAvailability(graph: CanvasGraph, zoom: number): Record<NavigationAction, { enabled: boolean; reason?: string }> {
  return {
    zoomIn: { enabled: zoom < MAX_CANVAS_ZOOM - 0.00001, reason: zoom >= MAX_CANVAS_ZOOM - 0.00001 ? "Maximum zoom reached." : undefined },
    zoomOut: { enabled: zoom > MIN_CANVAS_ZOOM + 0.00001, reason: zoom <= MIN_CANVAS_ZOOM + 0.00001 ? "Minimum zoom reached." : undefined },
    actualSize: { enabled: true },
    fitBoard: { enabled: navigationTargets(graph, false).length > 0, reason: "Requires visible nodes with usable dimensions." },
    fitSelection: { enabled: navigationTargets(graph, true).length > 0, reason: "Select visible nodes or a connector with visible endpoints." },
  };
}
